import type {
  Checklist,
  ChecklistSnapshotItem,
  ChecklistState,
  Theme,
  Traits,
} from './types';
import { targetingApplies } from './runtime';
import { fireCompletionAnimation } from './animations';
import { renderInlineMarkdownInto } from './markdown';

// ---------- Module state ----------
//
// The checklist runtime mounts inside its own shadow root so its CSS can't
// leak into (or be polluted by) the host page or the tour shadow root. We
// keep at most one widget on screen at a time - if multiple checklists are
// published in the workspace, we render the first one and ignore the rest.
// This matches the MVP scope ("one active checklist per workspace") and
// avoids any UX question about which one to show.

type WidgetState = {
  host: HTMLElement | null;
  shadow: ShadowRoot | null;
  current: Checklist | null;
  expanded: boolean;
  resizeObserver: (() => void) | null;
  theme: Theme | null;
  // All checklists the API delivered for this end user. We re-evaluate
  // targeting against this list whenever URL or traits change so the widget
  // can appear/disappear without a full session reload.
  available: Checklist[];
  traits: Traits;
};

const widget: WidgetState = {
  host: null,
  shadow: null,
  current: null,
  expanded: false,
  resizeObserver: null,
  theme: null,
  available: [],
  traits: {},
};

// First checklist whose targeting matches the current URL + traits, or null
// if none match. Mirrors the AND-ed targeting model used for tours.
function pickEligible(
  checklists: Checklist[],
  url: string,
  traits: Traits,
): Checklist | null {
  for (const c of checklists) {
    if (targetingApplies(c.snapshot.targeting, url, traits)) return c;
  }
  return null;
}

export type ChecklistCallbacks = {
  /** Persist a single item completion. SDK calls this on auto + manual ticks. */
  reportComplete: (
    checklistId: string,
    itemId: string,
    source: 'auto' | 'manual',
  ) => void;
  /** Mark widget dismissed for this end user. */
  reportDismiss: (checklistId: string) => void;
  /** First-time view (per end user, per checklist). Idempotent server-side. */
  reportView: (checklistId: string) => void;
  /** Launch a Steppr tour by id. Used by the 'tour' action type. */
  launchTour: (tourId: string) => void;
};

let callbacks: ChecklistCallbacks | null = null;

const SHADOW_CSS = `
:host {
  font-size: 14px;
  line-height: 1.5;
  color: #0f172a;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
* {
  box-sizing: border-box;
  font-family: var(--steppr-font-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif);
}
button { font: inherit; cursor: pointer; }

.launcher {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 2147483600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  background: var(--steppr-primary, #2563eb);
  color: #ffffff;
  font-weight: 600;
  font-size: 13px;
  border: none;
  box-shadow: 0 8px 20px -8px rgba(0, 0, 0, 0.35);
  transition: filter 120ms ease, transform 120ms ease;
}
.launcher:hover { filter: brightness(1.08); transform: translateY(-1px); }
.launcher .count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  font-size: 11px;
  font-weight: 700;
}

.panel {
  position: fixed;
  bottom: 80px;
  right: 20px;
  z-index: 2147483601;
  width: 360px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 120px);
  background: #ffffff;
  border-radius: var(--steppr-radius, 12px);
  box-shadow: 0 24px 48px -16px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 16px 18px 12px;
  border-bottom: 1px solid #f1f5f9;
}
.panel-title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.005em;
}
.panel-progress {
  font-size: 12px;
  color: #64748b;
}
.panel-progress-bar {
  margin-top: 8px;
  height: 6px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
}
.panel-progress-fill {
  height: 100%;
  background: var(--steppr-primary, #2563eb);
  border-radius: 999px;
  transition: width 240ms ease;
}

.panel-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  border-radius: 6px;
  font-size: 18px;
  line-height: 1;
}
.panel-close:hover { background: #f1f5f9; color: #0f172a; }

.items {
  list-style: none;
  margin: 0;
  padding: 8px 0;
  overflow-y: auto;
  flex: 1;
}
.item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 18px;
  cursor: pointer;
  transition: background 120ms ease;
}
.item:hover { background: #f8fafc; }
.item-check {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  transition: background 120ms ease, border-color 120ms ease;
}
.item.done .item-check {
  background: var(--steppr-primary, #2563eb);
  border-color: var(--steppr-primary, #2563eb);
}
.item-check svg { width: 12px; height: 12px; color: #ffffff; opacity: 0; }
.item.done .item-check svg { opacity: 1; }

.item-body { flex: 1; min-width: 0; }
.item-title {
  font-size: 13.5px;
  font-weight: 500;
  color: #0f172a;
  line-height: 1.35;
}
.item.done .item-title { color: #94a3b8; text-decoration: line-through; }
.item-desc {
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.item-action {
  font-size: 11px;
  font-weight: 600;
  color: var(--steppr-primary, #2563eb);
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.item.done .item-action { color: #94a3b8; }

.powered {
  padding: 8px 18px 12px;
  text-align: right;
  font-size: 11px;
  color: #94a3b8;
  border-top: 1px solid #f1f5f9;
}
.powered a { color: inherit; text-decoration: none; }
.powered a:hover { text-decoration: underline; }
`;

function ensureShadow(): ShadowRoot {
  if (widget.shadow) return widget.shadow;
  const host = document.createElement('div');
  host.id = '__steppr_checklist_root__';
  host.style.all = 'initial';
  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = SHADOW_CSS;
  shadow.appendChild(style);
  document.body.appendChild(host);
  widget.host = host;
  widget.shadow = shadow;
  if (widget.theme) applyTheme(widget.theme);
  return shadow;
}

function applyTheme(theme: Theme) {
  widget.theme = theme;
  if (!widget.shadow) return;
  let style = widget.shadow.getElementById('steppr-checklist-theme') as
    | HTMLStyleElement
    | null;
  if (!style) {
    style = document.createElement('style');
    style.id = 'steppr-checklist-theme';
    widget.shadow.appendChild(style);
  }
  const radius = theme.radius === 'sm' ? '6px' : theme.radius === 'md' ? '10px' : '14px';
  const fontFamily =
    theme.fontFamily === 'inherit'
      ? 'inherit'
      : "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
  style.textContent = `:host { --steppr-primary: ${theme.primaryColor}; --steppr-radius: ${radius}; --steppr-font-family: ${fontFamily}; }`;
}

function urlMatches(pattern: string, url: string): boolean {
  // Checklist URL items use 'contains' semantics by design - same matcher as
  // the SDK uses for tour url_match triggers when the customer picked
  // 'contains'. Keeps the customer's mental model simple: paste a path
  // fragment, the item ticks when the user lands somewhere matching it.
  return url.indexOf(pattern) !== -1;
}

function isItemDone(itemId: string): boolean {
  const c = widget.current;
  if (!c) return false;
  return c.state.items[itemId] !== undefined;
}

function progress(): { done: number; total: number } {
  const c = widget.current;
  if (!c) return { done: 0, total: 0 };
  let done = 0;
  for (const item of c.snapshot.items) {
    if (isItemDone(item.id)) done++;
  }
  return { done, total: c.snapshot.items.length };
}

function renderLauncher(shadow: ShadowRoot): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'launcher';
  btn.type = 'button';
  const { done, total } = progress();
  btn.innerHTML =
    '<span class="label">Getting started</span>' +
    '<span class="count">' +
    done +
    ' / ' +
    total +
    '</span>';
  btn.addEventListener('click', () => toggle());
  return btn;
}

function renderItem(item: ChecklistSnapshotItem): HTMLLIElement {
  const li = document.createElement('li');
  li.className = 'item' + (isItemDone(item.id) ? ' done' : '');

  const check = document.createElement('span');
  check.className = 'item-check';
  // Inline SVG keeps the tick crisp at any DPI without depending on a font.
  check.innerHTML =
    '<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2 6.5L4.5 9L10 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  li.appendChild(check);

  const body = document.createElement('div');
  body.className = 'item-body';
  const title = document.createElement('div');
  title.className = 'item-title';
  title.textContent = item.title;
  body.appendChild(title);
  if (item.description) {
    const desc = document.createElement('div');
    desc.className = 'item-desc';
    renderInlineMarkdownInto(desc, item.description);
    body.appendChild(desc);
  }
  // Action affordance: tour items show "Start tour", url items show
  // "Open page". Event items expose only the manual checkmark (no
  // discoverable action) - those should auto-tick when the customer's
  // app fires the matching steppr.track() call.
  if (!isItemDone(item.id)) {
    if (item.actionType === 'tour') {
      const a = document.createElement('div');
      a.className = 'item-action';
      a.textContent = 'Start tour →';
      body.appendChild(a);
    } else if (item.actionType === 'url') {
      const a = document.createElement('div');
      a.className = 'item-action';
      a.textContent = 'Open page →';
      body.appendChild(a);
    }
  }
  li.appendChild(body);

  li.addEventListener('click', () => onItemClick(item));
  return li;
}

function onItemClick(item: ChecklistSnapshotItem) {
  const c = widget.current;
  if (!c || !callbacks) return;
  if (isItemDone(item.id)) return; // already done - no-op

  if (item.actionType === 'tour') {
    callbacks.launchTour(item.actionValue);
    return;
  }
  if (item.actionType === 'url') {
    // Same-tab navigation (sometimes a different domain, often a route in the
    // same SPA). The actionValue is a substring used by the auto-tick logic
    // on subsequent route_change observation; if it doesn't start with http,
    // we treat it as a path on the current origin.
    try {
      const dest = item.actionValue.match(/^https?:\/\//)
        ? item.actionValue
        : item.actionValue.startsWith('/')
          ? item.actionValue
          : '/' + item.actionValue;
      window.location.href = dest;
    } catch {
      // Bad URL - fall through to manual-tick the item so the user isn't
      // stuck with a broken row.
      completeItem(item.id, 'manual');
    }
    return;
  }
  // Event items: clicking is the manual escape hatch - unless the
  // checklist is configured to forbid manual completion, in which case
  // the item only ticks when the matching steppr.track() event fires.
  if (c.snapshot.config?.disableManualCompletion === true) return;
  completeItem(item.id, 'manual');
}

function completeItem(itemId: string, source: 'auto' | 'manual') {
  const c = widget.current;
  if (!c) return;
  if (c.state.items[itemId]) return; // already done - debounce locally

  // Snapshot the all-done state BEFORE we tick so we can detect the
  // transition cleanly. A re-tick of an already-done item would otherwise
  // re-fire the animation - completedItems above already short-circuits
  // that, so this is just defence-in-depth.
  const wasComplete = isAllDone(c);

  // Optimistic local update so the UI ticks instantly. Server is the source
  // of truth on next session boot, but for in-session UX the local state is
  // canonical.
  c.state.items[itemId] = {
    completedAt: new Date().toISOString(),
    source,
  };
  if (callbacks) callbacks.reportComplete(c.id, itemId, source);
  rerender();

  // Fire the completion animation exactly on the wasn't-done → all-done
  // edge. Reading `c` again after rerender is fine - we hold the same
  // reference; rerender doesn't swap the widget's current checklist.
  if (!wasComplete && isAllDone(c)) {
    fireCompletionAnimation(c.snapshot.config, widget.theme?.primaryColor);
  }
}

function isAllDone(c: Checklist): boolean {
  if (c.snapshot.items.length === 0) return false;
  return c.snapshot.items.every((i) => c.state.items[i.id] !== undefined);
}

function rerender() {
  if (!widget.shadow || !widget.current) return;
  const shadow = widget.shadow;
  // Clear non-style nodes.
  const styles = Array.from(shadow.querySelectorAll('style'));
  shadow.replaceChildren(...styles);

  shadow.appendChild(renderLauncher(shadow));

  if (widget.expanded) {
    const panel = document.createElement('div');
    panel.className = 'panel';

    const header = document.createElement('div');
    header.className = 'panel-header';
    const close = document.createElement('button');
    close.className = 'panel-close';
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';
    close.addEventListener('click', () => dismiss());
    panel.appendChild(close);

    const title = document.createElement('div');
    title.className = 'panel-title';
    title.textContent = widget.current.name;
    header.appendChild(title);

    const { done, total } = progress();
    const progressLine = document.createElement('div');
    progressLine.className = 'panel-progress';
    progressLine.textContent =
      total === 0
        ? 'No items yet'
        : done === total
          ? 'All done - nice work.'
          : done + ' of ' + total + ' complete';
    header.appendChild(progressLine);

    if (total > 0) {
      const bar = document.createElement('div');
      bar.className = 'panel-progress-bar';
      const fill = document.createElement('div');
      fill.className = 'panel-progress-fill';
      fill.style.width = Math.round((done / total) * 100) + '%';
      bar.appendChild(fill);
      header.appendChild(bar);
    }
    panel.appendChild(header);

    const ul = document.createElement('ul');
    ul.className = 'items';
    for (const item of widget.current.snapshot.items) {
      ul.appendChild(renderItem(item));
    }
    panel.appendChild(ul);

    if (!widget.theme?.hideBranding) {
      const powered = document.createElement('div');
      powered.className = 'powered';
      powered.innerHTML =
        'Powered by <a href="https://steppr.io" target="_blank" rel="noopener noreferrer">Steppr</a>';
      panel.appendChild(powered);
    }

    shadow.appendChild(panel);
  }
}

function toggle() {
  widget.expanded = !widget.expanded;
  rerender();
}

function dismiss() {
  if (!widget.current || !callbacks) return;
  callbacks.reportDismiss(widget.current.id);
  // Local state: hide the panel, keep the launcher (so end users can re-open
  // the checklist). The dismiss endpoint suppresses any server-side
  // auto-popup logic we add later.
  widget.expanded = false;
  rerender();
}

// ---------- Public API ----------

export function bootChecklist(
  checklists: Checklist[],
  cb: ChecklistCallbacks,
  theme: Theme,
  url: string,
  traits: Traits,
) {
  callbacks = cb;
  widget.theme = theme;
  widget.available = checklists;
  widget.traits = traits;
  showEligible(url);
}

// Re-evaluate targeting against the current url + traits and swap the active
// widget if the eligible checklist changed. Cheap to call on every route
// change / identify(); no-ops when targeting outcome is unchanged.
function showEligible(url: string) {
  const next = pickEligible(widget.available, url, widget.traits);

  // Same checklist as before - nothing to do.
  if (next?.id === widget.current?.id) {
    if (next === null) teardownChecklist();
    return;
  }

  // Either no longer eligible, or a different checklist becomes eligible.
  if (next === null) {
    teardownChecklist();
    return;
  }

  // Tear down any prior widget DOM before mounting a new one. State (callbacks,
  // theme, available, traits) survives - those are module-scoped.
  if (widget.host) {
    widget.host.remove();
    widget.host = null;
    widget.shadow = null;
  }
  widget.current = next;
  widget.expanded = false;
  ensureShadow();
  // Per-flow theme baked into the snapshot at publish wins over the
  // workspace default applied on session boot. Falls back to the
  // session theme when the snapshot doesn't carry one (older
  // checklists from before themes-as-presets shipped).
  const theme = next.snapshot.theme ?? widget.theme;
  if (theme) applyTheme(theme);

  // Fire view exactly once per (endUser, checklist). Server dedupes via the
  // existence of an end_user_checklist_state row; we still call eagerly so
  // the count reflects real visibility, not just snapshot delivery.
  if (callbacks) callbacks.reportView(next.id);

  rerender();
}

export function teardownChecklist() {
  if (widget.host) {
    widget.host.remove();
  }
  widget.host = null;
  widget.shadow = null;
  widget.current = null;
  widget.expanded = false;
  widget.theme = null;
  widget.available = [];
  widget.traits = {};
}

// Called by the client whenever a tour completes. Auto-ticks any 'tour'
// items pointing at this tour id.
export function onTourComplete(tourId: string) {
  const c = widget.current;
  if (!c) return;
  for (const item of c.snapshot.items) {
    if (item.actionType === 'tour' && item.actionValue === tourId) {
      completeItem(item.id, 'auto');
    }
  }
}

// Called by the client on every route change observed by the runtime
// (history pushState/popstate patches). Two responsibilities:
//   1. Re-evaluate checklist targeting - show/hide widget if the new URL
//      flips a `url_match` rule's outcome.
//   2. Auto-tick `url` items whose actionValue is contained in the new URL
//      on the currently-visible checklist.
export function onRouteChange(url: string) {
  showEligible(url);
  const c = widget.current;
  if (!c) return;
  for (const item of c.snapshot.items) {
    if (item.actionType === 'url' && urlMatches(item.actionValue, url)) {
      completeItem(item.id, 'auto');
    }
  }
}

// Called when the customer's app provides new traits via Steppr.identify().
// Targeting may flip - e.g., a checklist gated on plan='pro' becomes visible
// once we learn the trait.
export function onTraitsChange(url: string, traits: Traits) {
  widget.traits = traits;
  showEligible(url);
}

// Called by the client whenever steppr.track() fires. Auto-ticks any 'event'
// items whose actionValue matches the event name.
export function onTrackEvent(name: string) {
  const c = widget.current;
  if (!c) return;
  for (const item of c.snapshot.items) {
    if (item.actionType === 'event' && item.actionValue === name) {
      completeItem(item.id, 'auto');
    }
  }
}

// Re-apply theme without rebuilding the widget (called when theme arrives
// after the widget already mounted, e.g., second session call).
export function applyChecklistTheme(theme: Theme) {
  applyTheme(theme);
  rerender();
}

/**
 * Open a checklist directly, bypassing targeting. Used by the Resource Center
 * "click to open" flow - the user has explicitly asked for this checklist, so
 * we show it even if URL/trait rules wouldn't otherwise match.
 *
 * If the chosen checklist is different from the current one, we tear the
 * existing widget down and remount with the new snapshot. Always expands.
 */
export function openChecklistById(id: string): void {
  const target = widget.available.find((c) => c.id === id);
  if (!target) return;
  if (widget.current?.id !== target.id) {
    if (widget.host) {
      widget.host.remove();
      widget.host = null;
      widget.shadow = null;
    }
    widget.current = target;
    ensureShadow();
    const theme = target.snapshot.theme ?? widget.theme;
    if (theme) applyTheme(theme);
    if (callbacks) callbacks.reportView(target.id);
  }
  widget.expanded = true;
  rerender();
}
